import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@common/lib/utils';

const STATUS_TABLE_WIDTH = 'tw-w-[60px] tw-justify-center';
const STATUS_CHIP_BASE = `tw-border-transparent ${STATUS_TABLE_WIDTH} tw-rounded-lg`;
const PROMO_CHIP_BASE = 'tw-border-transparent tw-w-auto tw-min-w-0 tw-whitespace-nowrap';
// On dark surfaces the "subtlest" brand tone sits below the card, so the chip
// reads as a hole. Step up one tone (and brighten the label) the way the
// sub-navigation and template pickers already do for brand tints in dark mode.
const PRIMARY_CHIP_BG = [
  'tw-bg-[var(--bg-brand-subtlest)] dark:tw-bg-[var(--bg-brand-subtle)]',
  'tw-text-[var(--icons-brand)] dark:tw-text-[var(--icons-brand-hover)]',
].join(' ');

const badgeVariants = cva(
  `tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-2.5 tw-leading-6 tw-py-0.5 tw-text-xs tw-font-semibold
  tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2`,
  {
    variants: {
      variant: {
        default:
          `tw-border-transparent tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary/80 ${STATUS_CHIP_BASE}`,
        secondary:
          'tw-border-transparent tw-bg-secondary tw-text-secondary-foreground hover:tw-bg-secondary/80 tw-rounded-lg',
        destructive:
          'tw-border-transparent tw-bg-destructive tw-text-destructive-foreground hover:tw-bg-destructive/80 tw-rounded-lg',
        outline: 'tw-text-foreground tw-rounded-lg',
        live: `tw-bg-blue-light tw-text-blue-custom ${STATUS_CHIP_BASE}`,
        completed: `tw-bg-green-light tw-text-green-custom ${STATUS_CHIP_BASE}`,
        paused: `tw-bg-orange-light tw-text-orange-custom ${STATUS_CHIP_BASE}`,
        archived: `tw-bg-gray-custom-400 tw-text-white ${STATUS_CHIP_BASE}`,
        draft: `tw-bg-gray-custom-100 tw-text-gray-custom-500 dark:tw-text-dark-paragraph-2 ${STATUS_CHIP_BASE}`,
        scheduled: `tw-bg-gray-custom-100 tw-text-gray-custom-500 ${STATUS_CHIP_BASE}`,
        deleted: `tw-bg-red-light tw-text-red-link ${STATUS_CHIP_BASE}`,
        new: `${PROMO_CHIP_BASE} ${PRIMARY_CHIP_BG}`,
        ai: `${PROMO_CHIP_BASE} tw-bg-gray-custom-800 tw-text-white`,
        'coming-soon': `${PROMO_CHIP_BASE} tw-bg-orange-light tw-text-orange-custom`,
        maintenance: `${PROMO_CHIP_BASE} tw-bg-red-light tw-text-red-link`,
        announcement: `${PROMO_CHIP_BASE} ${PRIMARY_CHIP_BG}`,
        beta: `${PROMO_CHIP_BASE} tw-bg-blue-light tw-text-blue-custom`,
        updated: `${PROMO_CHIP_BASE} tw-bg-green-light tw-text-green-custom`,
        limited: 'tw-w-auto tw-min-w-0 tw-whitespace-nowrap tw-border-orange-custom tw-bg-transparent tw-text-orange-custom',
      },
      size: {
        default: '',
        xs: '!tw-px-1 !tw-py-0 !tw-w-auto !tw-min-w-0 tw-text-[9px] tw-leading-[14px] tw-tracking-wide !tw-rounded-sm',
        sm: '!tw-px-1.5 !tw-py-0 !tw-w-auto !tw-min-w-0 tw-text-[10px] tw-leading-4 tw-tracking-wide !tw-rounded-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
  VariantProps<typeof badgeVariants> { }

function Badge({ className, variant, size, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
